import { FetchError } from "@simbelapp/order-sdk";
import { HttpStatusEnum } from "~/utils/enums/common.enums";
import type { ISdkApiError } from "~/utils/interfaces/common-interfaces";

/**
 * HTTP status of a rejected call. An SDK `ResponseError` carries it on `response`, an error already
 * normalised by `toSdkApiError` or by a store carries it on itself.
 */
export function getErrorStatus(error: unknown): number | undefined {
  const rejected = error as { response?: { status?: number }; status?: number } | null | undefined;
  return rejected?.response?.status ?? rejected?.status;
}

/**
 * Whether a rejected call got no HTTP answer: network down, page unloading, or a proxy 5xx the browser hides
 * for lack of CORS headers. The SDK raises a `FetchError` then, which `toSdkApiError` keeps as `cause`.
 */
export function isNoResponseError(error: unknown): boolean {
  return error instanceof FetchError || (error instanceof Error && error.cause instanceof FetchError);
}

/**
 * Statuses meaning "this resource is out of reach for this user" — deleted, or outside their scope.
 *
 * 401 belongs in the list: the orders `ForbiddenExceptionFilter` answers both `ForbiddenException`
 * and `UnauthorizedException` with 401 and keeps the real status only in the body's `statusCode`,
 * so a guard matching on 403 alone is dead code. Answer these with a terminal "no longer
 * accessible" screen rather than an error toast — the user is not looking at a failure they can
 * retry.
 */
export function isOutOfReachStatus(status: number | undefined): boolean {
  return (
    status === HttpStatusEnum.UNAUTHORIZED || status === HttpStatusEnum.FORBIDDEN || status === HttpStatusEnum.NOT_FOUND
  );
}

/** Body keys that must not overwrite the Error's own identity when copied onto it. */
const RESERVED_ERROR_KEYS = new Set(["name", "stack", "message"]);

/** Shown when the body names nothing: there is no machine code to translate. */
const DEFAULT_ERROR_MESSAGE = "Une erreur est survenue";

/** The body's `message` as one string — Nest answers an array when validation is what failed. */
function readBodyMessage(body: unknown): string | undefined {
  const message = (body as { message?: unknown } | null | undefined)?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) {
    const parts = message.filter((part): part is string => typeof part === "string");
    if (parts.length) return parts.join(", ");
  }
  return undefined;
}

/**
 * Normalise a rejected SDK call into ONE error carrying both halves of the answer.
 *
 * The two helpers used to keep a different half each — the proxy rethrew the body and dropped the
 * status, the older helper kept the `ResponseError` with its body unread — so a 400's machine code
 * was unreadable from whichever one the caller happened to use. Here the status lands on `status`,
 * the parsed body on `body`, and the body's own keys are copied on top so `errorCode`, `code` and
 * `data` stay readable. `response` is left with its body **undisturbed** (the proxy parses a clone),
 * so a caller already reading `error.response.json()` keeps working. The rejection itself stays on
 * `cause`: when no answer came back, it is the only place the browser's reason survives.
 */
export function toSdkApiError(error: unknown, body: unknown): ISdkApiError {
  const rejected = error as { response?: Response; status?: number; message?: unknown } | null | undefined;
  const message =
    readBodyMessage(body) ??
    (typeof body === "string" && body ? body : undefined) ??
    (typeof rejected?.message === "string" ? rejected.message : undefined) ??
    DEFAULT_ERROR_MESSAGE;

  const sdkError = new Error(message, { cause: error }) as ISdkApiError;
  sdkError.name = "SdkApiError";
  sdkError.status = getErrorStatus(error);
  sdkError.response = rejected?.response;
  sdkError.body = body;

  if (body && typeof body === "object") {
    for (const [key, value] of Object.entries(body)) {
      if (!RESERVED_ERROR_KEYS.has(key)) sdkError[key] = value;
    }
  }

  return sdkError;
}

/** The rejected call's body, parsed off a **clone** so the caller can still read the response. */
export async function readErrorBody(error: unknown): Promise<unknown> {
  const response = (error as { response?: Response } | null | undefined)?.response;
  if (typeof response?.json !== "function") return null;

  try {
    const readable = typeof response.clone === "function" ? response.clone() : response;
    return await readable.json();
  } catch {
    return null;
  }
}
